import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-4691" />} data-id="element-4690">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4692">
        <motion.div variants={itemVariants} data-id="element-4693">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-4695" />} data-id="element-4694" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4696">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4698" />} data-id="element-4697" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4699">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4701" />} data-id="element-4700" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4702">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4704" />} data-id="element-4703" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4705">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4706">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4707">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4708">
            <div className="flex items-center justify-between" data-id="element-4709">
              <div className="flex items-center gap-2" data-id="element-4710">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4711" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4712">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4713">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4714">
              <div className="flex items-center gap-2" data-id="element-4715">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4716" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4717">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4718">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4719" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4720" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4721">
            <div className="flex items-center justify-between" data-id="element-4722">
              <div className="flex items-center gap-3" data-id="element-4723">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4724" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4725" />}
                <div data-id="element-4726">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4727">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4728">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4729">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4730" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4731">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4732">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4733" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4734" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4735">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4736">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4737">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4738">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4739">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4740">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4741">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4742">
                    <div className="text-lg font-bold text-red-700" data-id="element-4743">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4744">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4745">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4746" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4747" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4748" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4749">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4750">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4751">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4752">
            <Clock className="w-4 h-4" data-id="element-4753" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4754">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4755">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4756">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4757" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4758" /> : <Activity className="w-4 h-4" data-id="element-4759" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4760">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4761">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4762">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4763">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4764">
            <Server className="w-4 h-4" data-id="element-4765" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4766">
            <div className="flex items-center justify-between" data-id="element-4767">
              <span className="text-sm text-gray-600" data-id="element-4768">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4769">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4770">
              <span className="text-sm text-gray-600" data-id="element-4771">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4772">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4773">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4774">
                <span className="text-sm text-gray-600" data-id="element-4775">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4776">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4777">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4778" />
              </div>
            </div>
            <div data-id="element-4779">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4780">
                <span className="text-sm text-gray-600" data-id="element-4781">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4782">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4783">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4784" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4785">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4786">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4787">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4788">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4789">
                <div className="flex justify-between text-xs mb-1" data-id="element-4790">
                  <span className="text-gray-600" data-id="element-4791">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4792">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4793">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4794" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4795">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4796">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4797" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4798">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4799">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4800">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4801">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4802">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4803">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4804">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4805">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4806">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4807" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4808">
            <div className="flex items-end justify-between" data-id="element-4809">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4810">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4811">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4812">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4813" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4814">
              <div className="flex justify-between" data-id="element-4815">
                <span className="text-gray-500" data-id="element-4816">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4817">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4818">
                <span className="text-gray-500" data-id="element-4819">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4820">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}